import { useMutation } from '@tanstack/react-query'

import { OrgACLAPI, TeamMembersAPI, TeamsAPI } from '@makyoapp/makyo-sdk'
import { OrgRules, TeamMemberRole, TeamRules } from '@makyoapp/makyo-sdk/models/ACL'
import { MemberAdapter } from '@makyoapp/makyo-common'
import { ImgEasyResult } from '@makyoapp/makyo-ui'

import { mutationKeys } from '@/lib/react-query/mutation-keys'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'

/**
 * This hook exposes a mutation function that allows to add a new team to the current organization.
 * It gets the current organization from the useCurrentOrgContext hook.
 * If the organization ID is not null, it calls the TeamsAPI.ReqAdd function to add a new team with the provided name and avatar.
 *
 * @returns A mutation function that takes an object with the new team's name and optionally an avatar and adds the team to the current organization.
 */
export const useAddNewTeamMutation = () => {
  const { currentOrg } = useCurrentOrgContext()
  const orgId = currentOrg?.id

  return useMutation({
    mutationKey: [mutationKeys.addNewTeam],
    mutationFn: async ({ nextName, avatar }: { nextName: string; avatar?: ImgEasyResult }) => {
      if (!orgId) return

      return await TeamsAPI.ReqAdd({
        _Name: nextName,
        //_Avatar: newTeamAvatar,
        _Avatar: avatar?.file !== undefined ? avatar?.file : null,
        _Description: '',
        _Members: [],
        _OrganizationID: currentOrg.id,
      })
    },
  })
}

/**
 * This hook exposes a mutation function that allows to add new members to a specific team.
 * If the team ID is not null, it prepares the payload for the new members,
 * then calls the TeamMembersAPI.ReqAdd function to add the new members to the specified team.
 *
 * @returns A mutation function that takes an object with the team's ID and an array of new members with their roles and adds the new members to the specified team.
 */
export const useAddMembersMutation = () =>
  useMutation({
    mutationKey: [mutationKeys.addTeamMembers],
    mutationFn: async ({
      teamId,
      newMembers,
    }: {
      teamId?: string
      newMembers: {
        member: MemberAdapter
        teamRole: TeamMemberRole
      }[]
    }) => {
      if (!teamId) return

      const newMembersPayload = newMembers.map((mo) => {
        return {
          _Email: mo.member.email,
          _Role: mo.teamRole,
        }
      })

      await TeamMembersAPI.ReqAdd(teamId, newMembersPayload)
    },
  })

/**
 * This hook exposes a mutation function that allows to update the role of a specific member in a specific team.
 * If the team ID is not null, it calls the TeamMembersAPI.ReqUpdateRole function to update the member's role in the specified team.
 *
 * @returns A mutation function that takes an object with the team's ID, the member's ID and the new role and updates the member's role in the specified team.
 */
export const useUpdateRoleMutation = () => {
  return useMutation({
    mutationKey: [mutationKeys.updateMemberRole],
    mutationFn: async ({
      teamId,
      memberId,
      newRole,
    }: {
      teamId?: string
      memberId: string
      newRole: TeamMemberRole
    }) => {
      if (!teamId) return

      await TeamMembersAPI.ReqUpdateRole(teamId, memberId, newRole)
    },
  })
}

/**
 * This hook exposes a mutation function that allows to remove a specific member from a specific team.
 * If the team ID is not null, it calls the TeamMembersAPI.ReqDelete function to remove the member from the specified team.
 *
 * @returns A mutation function that takes an object with the team's ID and the member's ID and removes the member from the specified team.
 */
export const useRemoveMemberMutation = () =>
  useMutation({
    mutationKey: [mutationKeys.removeMember],
    mutationFn: async ({ teamId, memberId }: { teamId?: string; memberId: string }) => {
      if (!teamId) return

      await TeamMembersAPI.ReqDelete(teamId, memberId)
    },
  })

/**
 * This hook exposes a mutation function that allows to update the access control list (ACL) of the current organization.
 * If the organization ID is not null, it calls the OrgACLAPI.ReqUpdate function to update the ACL of the specified organization.
 *
 * @returns A mutation function that takes an object with the organization's ACL and the rules and updates the ACL of the specified organization.
 */
export const useUpdateACLMutation = () => {
  const { currentOrg } = useCurrentOrgContext()
  const orgId = currentOrg?.id
  return useMutation({
    mutationKey: [mutationKeys.org.updateAcl],
    mutationFn: async ({ org, team }: { org: OrgRules; team: TeamRules }) => {
      if (!orgId) return

      await OrgACLAPI.ReqUpdate(currentOrg.id, {
        _Organization: org,
        _Team: team,
      })
    },
  })
}
